<template>
    <div class="dsc-page">
        <div class="card dsc-card">
            <div class="dsc-header">
                <TitleButton
                    :showBtn="true"
                    :showAddNew="false"
                    title="Daily Sales Collection"
                    @onClickCloseButton="goToList"
                />
            </div>

            <div class="dsc-body">
                <section class="dsc-section">
                    <div class="dsc-section-head">
                        <i class="fas fa-user-circle dsc-section-icon"></i>
                        <span class="dsc-section-title">Customer & Delivery</span>
                    </div>
                    <div class="row g-3">
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="dsc-label">Customer Name</label>
                            <AsyncSelect
                                placeholder="Select Customer Name"
                                v-model="contactProfile"
                                :api-service="fetchContactProfiles"
                            />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="dsc-label">PO Reference</label>
                            <vField
                                name="reference"
                                type="text"
                                class="form-control"
                                placeholder="PO Reference"
                                v-model="formData.order_number"
                            />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="dsc-label">Ship From (Warehouse)</label>
                            <v-select
                                placeholder="Select Warehouse Location"
                                v-model="formData.location_id"
                                :options="locations"
                                label="text"
                                :reduce="text => text.id"
                            />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="dsc-label">Ship To</label>
                            <vField
                                as="textarea"
                                name="destination_address"
                                rows="1"
                                type="text"
                                class="form-control"
                                placeholder="Ship To"
                                v-model="formData.destination_address"
                            />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="dsc-label">Transport Type &amp; Number</label>
                            <vField
                                name="transport_reference"
                                type="text"
                                class="form-control"
                                placeholder="Transport Type & Number"
                                v-model="formData.transport_reference"
                            />
                        </div>
                        <div class="col-12 col-sm-6 col-lg-4">
                            <label class="dsc-label">VAT Type</label>
                            <v-select
                                placeholder="Select Vat Type"
                                v-model="formData.vat_type"
                                :options="vatTypeList"
                                label="name"
                                :reduce="name => name.value"
                            />
                        </div>
                    </div>
                </section>

                <section class="dsc-section">
                    <div class="dsc-section-head">
                        <i class="fas fa-boxes dsc-section-icon"></i>
                        <span class="dsc-section-title">Add Products</span>
                    </div>
                    <div class="row g-3 align-items-end">
                        <div class="col-12 col-md-7 col-lg-8">
                            <label class="dsc-label">Product</label>
                            <AsyncSelect
                                ref="productSelectRef"
                                placeholder="Select Product"
                                v-model="selectedProduct"
                                :api-service="fetchProductList"
                                :format-label="option => option.text"
                                :additional-query="{ product_types: ['finished_goods', 'services'] }"
                                label="text"
                            />
                        </div>
                        <div class="col-12 col-md-5 col-lg-4">
                            <label class="dsc-label">Quantity <span class="dsc-hint">(press Enter to add)</span></label>
                            <vField
                                ref="quantityFieldRef"
                                name="quantity"
                                type="number"
                                class="form-control"
                                placeholder="Quantity"
                                v-model="quantity"
                                @keydown.enter.prevent="onQuantityEnter"
                            />
                        </div>
                    </div>
                </section>

                <section class="dsc-section dsc-section--table">
                    <DailySalesCollectionTable
                        :tableItems="tableItems"
                        :discount-account-heads="discountAccountHeads"
                        @addNewItem="addNewItem"
                        @handleSubmit="handleSubmit"
                        :submitLoading="submitLoading"
                        :isUpdate="true"
                    />
                </section>
            </div>
        </div>

        <GlobalLoader />
        <Loader v-if="loader"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import DailySalesCollectionTable from '@/components/molecule/company/inventory/daily-sales-collection/DailySalesCollectionTable.vue';
import {onMounted, ref, inject, provide, watch, computed, nextTick} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import { useStore } from "vuex";
import useDate from "@/services/utils/day";
import handlePurchase from "@/services/modules/purchase";
import handleCompany from "@/services/modules/company";
import handleBusinessesLocations from "@/services/modules/businessesLocations";
import hospitalPdfPrinterHelper from "@/services/utils/hospitalPdfPrinterHelper";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import Loader from '@/components/atom/LoaderComponent'
import handleContact from '@/services/modules/contact';
import handleInventory from "@/services/modules/inventory";
import handleBarcode from '@/services/modules/barcode';
import pdfPrinter from '@/services/utils/pdfPrinter';
import handleHospital from '@/services/modules/hospital';
import handleChartAccount from '@/services/modules/chartAccount'

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { fetchSalesBill, fetchProductList, ...rest } = handlePurchase();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const {fetchBusinessLocationsList} = handleBusinessesLocations()
const { generatePdf } = hospitalPdfPrinterHelper();
const {currentDate, formatDate} = useDate();
const { fetchContactProfiles } = handleContact();
const { getProduct } = handleInventory()
const { exportToPDF } = pdfPrinter();
const { fetchBarcode } = handleBarcode();
const { notesByType } = handleHospital();
const { fetchHome } = handleChartAccount()

const itemSkeleton = {
    name: '',
    description: '',
    quantity: null,
    rate: 0,
    discount_percent: 0,
    vat: 0,
    sub_total: 0,
    discount_amount: 0,
    vat_amount: 0,
    total: 0,
    amount: 0,
    total_amount: 0,
    is_edit: false,
};

const contactProfile = ref(null);
const company = ref({});
const locations = ref([]);
const tableItems = ref([]);
const discountAccountHeads = ref([]);
const submitLoading = ref(false);
const invoiceRes = ref(null);
const loader = ref(false);
const selectedProduct = ref(null);
const quantity = ref(null);
const productSelectRef = ref(null);
const quantityFieldRef = ref(null);
const companyInfo = ref({});
const invoiceNotes = ref([]);
const barcodeQuery = ref('');
const barcode = ref('');
const qrcode = ref('');
const customizationPdfFields = ref([]);
const vatTypeList = ref([]);
const companyId = $route.params.companyId;
const user = computed(() => $store.state.user.user);

const getInitialForm = () => {
    return {
        company_id: companyId,
        contact_profile_id: null,
        account_head_id: null,
        ipd_register_id: null,
        vat_payable_account_head_id: null,
        project_id: null,
        cost_centre_id: null,
        location_id: null,
        mop_account_head_id: null,
        discount_head_id: null,
        service_resource_id: null,
        receipt_reference: null,
        adjustment_amount: null,
        referrer_id: null,
        receipt_description: "Daily sales collection",
        payment: 0,
        status: "active",
        date: currentDate(),
        has_item_detail: true,
        description: "",
        item_details: [],
        sale_type: "general_invoice",
        receipt_type: "general_invoice",
        order_number: '',
        destination_address: '',
        transport_reference: '',
        vat_type: '',
        mop_receipts: [],
    }
}

const formData = ref(getInitialForm());

provide('formData', formData);

const addNewItem = async () => {
    if (!selectedProduct.value) {
        showError("Please select a product");
        return;
    }

    const option = selectedProduct.value;
    const found = !!tableItems.value.find(item => item.product_id === option.id);
    if (found) {
        showError('Item already selected!');
        return;
    }

    if (option.product_type === 'services') {
        addServiceItem(option);
        return;
    }

    await addFinishedGoodsItem(option);
};

const addServiceItem = (option) => {
    const rate = option.description && option.description.sales_price ? option.description.sales_price : null;

    tableItems.value.push(buildTableItem({
        product_id: option.id,
        name: option.text,
        product_type: option.product_type,
        description: '',
        rate,
        vat: null,
        product_name: option.text
    }));

    afterAddItem();
};

const addFinishedGoodsItem = async (option) => {
    const productResp = await getProduct({
        id: option.id,
        company_id: companyId
    });

    if (!productResp.data) {
        showError("Something went wrong. Please try again.");
        return;
    }

    const selected = productResp.data;
    const unitLabel = selected.description.unit.description ? ` (${selected.description.unit.description})` : '';

    tableItems.value.push(buildTableItem({
        product_id: selected.id,
        name: selected.name + unitLabel,
        product_type: selected.product_type,
        description: selected.description.unit.description || '',
        rate: selected.description.sales_price ? selected.description.sales_price : null,
        vat: selected.description.vat_rate ? selected.description.vat_rate : null,
        product_name: selected.name
    }));

    afterAddItem();
};

const buildTableItem = ({ product_id, name, product_type, description, rate, vat, product_name }) => {
    return {
        product_id,
        name,
        product_type,
        description,
        quantity: quantity.value || 1,
        rate,
        discount: 0,
        vat,
        sub_total: 0,
        discount_amount: 0,
        discount_percent: null,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        product: {
            name: product_name
        }
    };
};

const afterAddItem = () => {
    selectedProduct.value = null;
    quantity.value = null;
    focusProductSelect();
};

const focusProductSelect = async () => {
    await nextTick();
    if (!productSelectRef.value) return;
    const vSelect = productSelectRef.value.dropdownRef;
    if (!vSelect) return;
    const searchInput = vSelect.$el.querySelector('input.vs__search');
    if (searchInput) searchInput.focus();
};

const focusQuantity = async () => {
    await nextTick();
    if (!quantityFieldRef.value) return;
    const input = quantityFieldRef.value.$el;
    if (!input) return;
    input.focus();
    input.select();
};

watch(selectedProduct, (newValue) => {
    if (!newValue) return;
    focusQuantity();
});

const onQuantityEnter = () => {
    if (!selectedProduct.value) {
        focusProductSelect();
        return;
    }
    addNewItem();
};

const goToList = () => {
    $router.push({
        name: 'daily-sales-collection',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId
        },
        query: $route.query
    })
}

const handleSubmit = async () => {
    const emptyQtyItem = tableItems.value.filter((item) => !item.quantity);
    if (emptyQtyItem && emptyQtyItem.length > 0) {
        showError("Item quantity cannot be empty");
        return;
    }

    if (!formData.value.date) formData.value.date = currentDate();
    formData.value.item_details = getFormattedTableItems()

    let copyFormData = Object.assign({}, formData.value);
    copyFormData.item_details = formData.value.item_details
    copyFormData.doctor_order_id = $route.params.orderId
    copyFormData.sale_type = "general_invoice"
    copyFormData.receipt_type = "general_invoice"
    copyFormData.receipt_generals = []
    copyFormData.mop_receipts = getFormattedMopReceipts()
    copyFormData.payment = copyFormData.mop_receipts
        .map(row => row.receipt_amount)
        .reduce((acc, val) => acc + val, 0)

    submitLoading.value = true;

    rest.updateSalesBill($route.params.saleId,  copyFormData)
    .then(res => {
        if(res.status) {
            showSuccess(res.message)
            goToPrint($route.params.saleId, 1)
            goToList()
        }
        if(!res.status) {
            showError(res.message)
        }
    })
    .catch( () => {
    })

    submitLoading.value = false
}

const getFormattedTableItems = () => {
    return tableItems.value.map((item) => {
        return {
            ...item,
            rate: item.rate || 0,
            vat: item.vat || 0,
            discount_percent: item.discount_percent || 0,
            amount: item.sub_total,
            total_amount: item.total,
            free_quantity: 0
        }
    })
}

const mapReceiptGeneralsToMopRows = (receiptGenerals) => {
    return receiptGenerals.map((general) => {
        const master = general.receipt_master ? general.receipt_master : {};
        return {
            receipt_master_id: master.id ? master.id : null,
            mop_account_head_id: master.account_head_id ? master.account_head_id : null,
            receipt_amount: general.paid_amount ? general.paid_amount : 0,
            receipt_reference: master.receipt_reference ? master.receipt_reference : '',
            note: master.description ? master.description : ''
        }
    })
}

const getFormattedMopReceipts = () => {
    return formData.value.mop_receipts
        .filter(row => row.mop_account_head_id && parseFloat(row.receipt_amount) > 0)
        .map((row) => {
            return {
                receipt_master_id: row.receipt_master_id ? row.receipt_master_id : null,
                mop_account_head_id: row.mop_account_head_id,
                receipt_amount: parseFloat(row.receipt_amount),
                receipt_reference: row.receipt_reference,
                note: row.note
            }
        })
}

onMounted(async () => {
  try {
    const res = await fetchCompanyInfo($route.params.companyId);
    companyInfo.value = res.data;
  } catch (error) {
  }

  await getInvoiceNotes();
  await getActiveFiledCustomization();
});

const getInvoiceNotes = async () => {
  const companyQuery = `?company_id=${$route.params.companyId}&show_default_only=1`;
  const res = await notesByType(companyQuery, 'invoice_terms');
  if (res.status) {
    invoiceNotes.value = res.data.filter((item) => item.full_desc);
  }
};

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${$route.params.companyId}&customization_type=pdf&type=sale_invoice_pdf_list`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const goToPrint = async (id, btnFunc) => {
  try {
    const companyQuery = `?company_id=${$route.params.companyId}`;
    const res = await fetchSalesBill(id, companyQuery);

    if (res) {
      invoiceRes.value = res.data;
      const qrcodeData = JSON.stringify({
        ref_no: invoiceRes.value.bill_number,
        ref_date: invoiceRes.value.date,
        created_by: invoiceRes.value.created_by_name,
        created_date_time: formatDate(new Date())
      });

      barcodeQuery.value = `?code=${invoiceRes.value.bill_number}&qrcode=${qrcodeData}`;

      const barcodeRes = await fetchBarcode(barcodeQuery.value);
      barcode.value = barcodeRes.barcode;
      qrcode.value = barcodeRes.qrcode;

      exportToPDF(
          companyInfo.value,
          invoiceRes.value,
          barcode.value,
          qrcode.value,
          user.value.name,
          btnFunc,
          invoiceNotes.value,
          false,
          customizationPdfFields.value
      );
    }
  } catch (err) {
  }
};

const setTableItems = (data) => {

    if(!data.general || data.general.length < 1) return;

    tableItems.value = data.general.map((item) => {
        return {
            ...itemSkeleton,
            product_id: item.product_id,
            name: item.product.name + '(Stock: ' + (item.stock_in_hand || 0) + ')',
            quantity: item.quantity || 0,
            rate: item.rate || 0,
            description: item.description || '',
            discount_percent: item.discount_percent || 0,
            discount_amount: item.discount_amount || 0,
            vat: item.vat || 0,
            vat_amount: item.vat_amount || 0,
            batch_number: item.batch_number
        }
    })
}

watch(contactProfile, (newValue) => {
    if (newValue) {
        formData.value.contact_profile_id = newValue.id;
        return;
    }

    formData.value.contact_profile_id = null;
});

onMounted(() => {
    const companyQuery = `?company_id=${companyId}`;
    const locationQuery = `${companyQuery}&location_type=fg_warehouse`;

    $store.commit('setLoading', true);

    Promise.all([
        fetchSalesBill($route.params.saleId, companyQuery).then(res => {
            if (!res.data) {
              return
            }

            Object.assign(formData.value, res.data);
            formData.value.order_number = res.data.order_number;
            formData.value.adjustment_amount = res.data.round_up_discount;
            formData.value.destination_address = res.data.destination_address;
            formData.value.transport_reference = res.data.transport_reference;
            formData.value.vat_type = res.data.vat_type;
            formData.value.payment = res.data.paid_amount

            if(res.data.receipt_generals && res.data.receipt_generals.length) {
                formData.value.mop_account_head_id = res.data.receipt_generals[0].receipt_master.account_head_id;
                formData.value.receipt_reference = res.data.receipt_generals[0].receipt_master.receipt_reference;
                formData.value.mop_receipts = mapReceiptGeneralsToMopRows(res.data.receipt_generals);
            }

            setTableItems(res.data);
            contactProfile.value = res.data.contact;

            if(res.data.discount_ledger && res.data.discount_ledger.account_head_id) {
                formData.value.discount_head_id = res.data.discount_ledger.account_head_id;
            }
        }),
        rest.fetchAccountReceivable(companyQuery).then(res => {
            if (res.data) formData.value.account_head_id = res.data[0].id;
        }),
        rest.fetchVatPayable(companyQuery).then(res => {
            if (res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
        }),
        fetchCompanyInfo(companyId).then(res => {
            if (res.data) company.value = res.data;
        }),
        rest.getAccountHeadBySlag("discount_account", companyQuery).then(res => {
            if (res.data) {
                discountAccountHeads.value = res.data;
                formData.value.discount_head_id = res.data.length ? res.data[0].id : null;
            }
        }),
        fetchBusinessLocationsList(locationQuery).then(res=> {
            if(res.data) {
                locations.value = res.data
            }
        }),
        fetchHome().then(res => {
          if (!res.data) {
            return;
          }
          if (res.data && res.data.vat_type){
            vatTypeList.value = res.data.vat_type;
          }
        })
    ])
    .finally(() => $store.commit('setLoading', false))
})
</script>

<style scoped>
address {
    margin-bottom: 0 !important;
}

.dsc-page {
    padding: 0.25rem;
}

.dsc-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 6px 24px rgba(34, 41, 47, 0.08);
    overflow: hidden;
}

.dsc-header {
    background: linear-gradient(135deg, #eef4ff 0%, #f6f9ff 100%);
    padding: 0.85rem 1.25rem;
    border-bottom: 1px solid #e6ebf5;
}

.dsc-body {
    padding: 1.25rem;
    background: #fbfcfe;
}

.dsc-section {
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 12px;
    padding: 1.1rem 1.25rem;
    margin-bottom: 1.1rem;
}

.dsc-section--table {
    padding: 0.5rem;
    background: transparent;
    border: none;
}

.dsc-section-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px dashed #e6ebf5;
}

.dsc-section-icon {
    color: #5a8dee;
    font-size: 1rem;
}

.dsc-section-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2f3a4a;
    letter-spacing: 0.2px;
}

.dsc-label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7488;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.dsc-hint {
    font-weight: 500;
    font-size: 0.7rem;
    text-transform: none;
    letter-spacing: 0;
    color: #9aa2b1;
}

.dsc-section :deep(.form-control) {
    border-radius: 8px;
}

.dsc-section :deep(.v-select .vs__dropdown-toggle) {
    border-radius: 8px;
}
</style>
